// src/hooks/cart/useAddToCart.ts

import { useMutation } from "@apollo/client";
import { useCallback } from "react";
import { ADD_TO_CART } from "../../mutations/AddToCart/addToCart.mutation";
import { GET_ACTIVE_ORDER, GET_TOTAL_LINES } from "../../queries/cart.queries";
import { resolveAddToCartResult } from "../../mutations/AddToCart/addToCart.resolver";

// =========================
// Types
// =========================

// What the hook returns to the UI
export type UseAddToCartResult =
  | { ok: true; type: "SUCCESS" }
  | {
      ok: true;
      type: "PARTIAL_SUCCESS";
      message: string;
      quantityAvailable: number;
    }
  | { ok: false; type: "ERROR"; message: string };

// =========================
// Hook
// =========================

export const useAddToCart = () => {
  const [mutate, { loading }] = useMutation(ADD_TO_CART, {
    awaitRefetchQueries: true,
    refetchQueries: [{ query: GET_TOTAL_LINES }, { query: GET_ACTIVE_ORDER }],
  });

  const addToCart = useCallback(
    async (
      variantId: string,
      quantity: number
    ): Promise<UseAddToCartResult> => {
      try {
        const res = await mutate({
          variables: {
            variantId,
            qty: quantity,
          },
        });

        if (!res.data?.addItemToOrder) {
          return {
            ok: false,
            type: "ERROR",
            message: "No response received from server.",
          };
        }

        const resolved = resolveAddToCartResult(res.data.addItemToOrder);

        switch (resolved.type) {
          case "SUCCESS":
            return { ok: true, type: "SUCCESS" };

          case "PARTIAL_SUCCESS":
            return {
              ok: true,
              type: "PARTIAL_SUCCESS",
              message: resolved.message,
              quantityAvailable: resolved.quantityAvailable,
            };

          case "ERROR":
            return {
              ok: false,
              type: "ERROR",
              message: resolved.message,
            };
        }
      } catch (e: any) {
        return {
          ok: false,
          type: "ERROR",
          message: e?.message ?? "Unexpected network error",
        };
      }
    },
    [mutate]
  );

  return {
    addToCart,
    loading,
  };
};
